/**
 * EmailConfirmationPrompt - UI component shown when user needs to confirm email
 * Used as fallback in RequireEmailConfirmed
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface EmailConfirmationPromptProps {
  /** Title to show */
  title?: string;
  /** Description text */
  description?: string;
  /** Whether to show back button */
  showBackButton?: boolean;
  /** Custom back handler */
  onBack?: () => void;
}

export function EmailConfirmationPrompt({
  title = 'Подтвердите email',
  description = 'Для этого действия необходимо подтвердить ваш email адрес.',
  showBackButton = true,
  onBack,
}: EmailConfirmationPromptProps) {
  const navigate = useNavigate();
  const { user, resendConfirmationEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleResend = useCallback(async () => {
    if (isResending || resendCooldown > 0) return;

    setIsResending(true);
    try {
      const { error } = await resendConfirmationEmail();
      if (error) {
        toast.error('Не удалось отправить письмо');
      } else {
        toast.success('Письмо отправлено');
        setResendCooldown(60);
        
        // Start cooldown timer
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } finally {
      setIsResending(false);
    }
  }, [isResending, resendCooldown, resendConfirmationEmail]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  }, [navigate, onBack]);

  const handleGoToConfirmation = useCallback(() => {
    navigate('/auth/email-confirmation', { state: { email: user?.email } });
  }, [navigate, user?.email]);

  return (
    <div className="min-h-screen bg-bg-primary flex flex-col">
      {/* Header */}
      {showBackButton && (
        <div className="p-4">
          <button
            onClick={handleBack}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-bg-secondary"
          >
            <ArrowLeft className="w-5 h-5 text-text-primary" />
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
        {/* Icon */}
        <div className="w-20 h-20 rounded-full bg-bg-accent-pink flex items-center justify-center mb-6">
          <Mail className="w-10 h-10 text-text-accent-pink" />
        </div>

        {/* Title */}
        <h1
          className="text-text-primary text-center mb-3"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontWeight: 900,
            fontSize: '24px',
            lineHeight: '120%',
          }}
        >
          {title}
        </h1>

        {/* Description */}
        <p
          className="text-text-tertiary text-center mb-6 max-w-sm"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontSize: '16px',
            lineHeight: '140%',
          }}
        >
          {description}
        </p>

        {/* Email */}
        {user?.email && (
          <p
            className="text-text-secondary text-center mb-8 font-medium"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '14px',
            }}
          >
            {user.email}
          </p>
        )}

        {/* Actions */}
        <div className="w-full max-w-sm space-y-3">
          {/* Go to confirmation screen */}
          <Button
            onClick={handleGoToConfirmation}
            className="w-full h-12"
            variant="default"
          >
            Ввести код
          </Button>

          {/* Resend button */}
          <Button
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            variant="outline"
            className="w-full h-12"
          >
            {isResending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : resendCooldown > 0 ? (
              `Отправить повторно (${resendCooldown}с)`
            ) : (
              'Отправить письмо повторно'
            )}
          </Button>

          {/* Back to catalog link */}
          <button
            onClick={() => navigate('/catalog')}
            className="w-full py-3 text-text-tertiary text-sm underline"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
            }}
          >
            Вернуться в каталог
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailConfirmationPrompt;
